// ============================================================================
// « GAMME COMPLÈTE » — l'assistant de création en six étapes (relevé maquette l.1458-2336).
//
// ⚠️ RELEVÉ SUR L'ARBRE DU DOM, pas au grep (Forme F du catalogue des défauts). C'est l'arbre qui dit
// que le corps est UNE grille `auto-fit minmax(230px,1fr)`, que le fil d'étapes est une rangée de
// pastilles reliées par un trait, et que le pied porte « Précédent » à gauche, « Annuler » puis
// l'action principale à droite.
//
// ⚠️ LES SIX ÉTAPES SONT SOLIDAIRES : un assistant dont « Suivant » ne mène nulle part est pire que pas
// d'assistant. Tant que les six ne sont pas construites, la porte reste FERMÉE dans le menu (mention
// « bientôt ») — ce fichier se construit derrière, sans être exposé.
//
// ⚠️ IL NE PARTAGE PAS L'ÉTAT DE « ARTICLE SIMPLE ». Les deux portes créent le même objet mais ne
// posent pas les mêmes questions : mutualiser leur brouillon obligerait chacune à porter les champs de
// l'autre, et le premier champ ajouté d'un côté apparaîtrait en creux dans l'autre.
// ============================================================================
import { proposeArticleCode } from '@orgazone/core-catalog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { confirm, Modal, OZ_INTERACTIVE_SURFACE, Picker, rovingRadio, showToast, Steps, Switch, Trans, useLatest } from '@orgazone/ui-web';
import type { ArticleFacet, ArticleFacets, SecondAxisOption, SizeGridOption } from '../api';
import { key, t, type Translatable } from '../i18n';
import { mulDivRound, rescale } from '@orgazone/core-money';
import { formatMoney, parseMinor, toInputValue } from '@orgazone/money-format';
import { api } from '../api';
import { useOfferedFacets } from './article-offres';
import { casesAAllouer, casesDeLaMatrice, cleCase, sizeAxes, variantsToCreate, matriceHorsBornes, type CaseMatrice, type SizeAxis } from './article-gamme-matrice';
import { achatTheoriqueCorner, indicateursPrix, ttcFromCoeff } from './prix-derive';
import { fieldLabel } from '../vocabulary';

/** Les six étapes, dans l'ordre — leurs libellés sont ceux du fil (relevé l.16768). */
export const ETAPES = [
  key('Identité'), key('Tailles & couleurs'), key('Codes-barres'), key('Prix'), key('Quantités'), key('Récapitulatif'),
] as const;

/** Le titre et la phrase d'accroche de chaque étape — relevés un à un. */
const HEADINGS: Record<number, { heading: Translatable; sous: Translatable }> = {
  1: { heading: key('De quel produit s’agit-il ?'), sous: key('Ces informations seront communes à toutes les déclinaisons.') },
  2: { heading: key('Quelles tailles, quelles couleurs ?'), sous: key('La grille donne les colonnes, vos couleurs donnent les lignes.') },
  3: { heading: key('Codes-barres'), sous: '' },
  4: { heading: key('À quel prix ?'), sous: '' },
  5: { heading: key('Combien, et pour aller où ?'), sous: '' },
  6: { heading: key('Tout est prêt'), sous: '' },
};

/** Ce que l'assistant collecte. Chaque étape n'en remplit qu'une part. */
export interface BrouillonGamme {
  nom: string;
  code: string;
  secteur: string;
  famille: string;
  brand: string;
  supplier: string;
  saison: string;
  tva: string;
  web: boolean;
  /** Étape 2 — la grille choisie ; ses tailles donnent les colonnes de la matrice. */
  grid: string;
  /**
   * Étape 2 — LE SECOND AXE choisi (longueurs, bonnets…) ; vide = un seul axe.
   * ⚠️ UN CHOIX, PAS UNE CONSÉQUENCE : il ne se déduit pas de la grille de tailles. Deux gammes bâties
   * sur « Lettres » n'ont aucune raison de se décliner pareil — c'est le modèle qui apparie.
   */
  grid2: string;
  /**
   * Les tailles ÉCARTÉES, et non les retenues.
   * ⚠️ Le défaut métier est « toute la grille » : stocker les retenues obligerait à les recopier
   * à chaque changement de grille, et un oubli rendrait la matrice vide sans que rien ne le dise.
   */
  droppedSizes: Record<string, true>;
  /** Étape 2 — les coloris retenus ; chacun donne une ligne de la matrice. */
  couleurs: string[];
  /** Étape 3 — d’où viennent les codes : du magasin, ou de la douchette. */
  bcMode: 'auto' | 'sup';
  /**
   * Étape 3 — le code de chaque case, indexé « couleur|taille ».
   * ⚠️ UNE CASE ABSENTE N’EST PAS CRÉÉE : c’est le seul moyen d’exclure une exception de la matrice.
   */
  codes: Record<string, string>;
  /**
   * Étape 3 — les cases VOLONTAIREMENT écartées de la matrice (le 38 qui n’existe pas en rouge).
   * ⚠️ STOCKÉES, et pas déduites de « case sans code » : sans cette liste, revenir à l’étape 2 puis
   * repasser ici rallouerait un code à ce qu’on venait d’exclure. Même idiome qu’à l’étape 2.
   */
  casesExclues: Record<string, true>;
  /** Étape 4 — régime d’achat : ferme (prix figé à l’entrée) ou corner (PA calculé à la vente). */
  corner: boolean;
  /** Étape 4 — prix d’achat HT saisi, en régime ferme (échelle fine à la lecture). */
  pa: string;
  /**
   * Étape 4 — le coefficient tapé.
   * ⚠️ OUTIL DE SAISIE, PAS UNE DONNÉE (même décision qu’à la fiche) : il produit un prix, et c’est
   * le PRIX qui part. Le stocker créerait une seconde vérité à côté du couple achat/vente.
   */
  coeff: string;
  /** Étape 4 — prix de vente TTC commun à la gamme. */
  pvBase: string;
  /** Étape 4 — le prix diffère selon la déclinaison (lingerie, grandes tailles…). */
  prixVariable: boolean;
  /** Étape 4 — prix propre à une case ; vide = le prix commun s’applique. */
  pvParCase: Record<string, string>;
  /**
   * Étape 5 — quantité par case, en pièces entières.
   * ⚠️ FACULTATIVES : on crée très souvent le référentiel d’une gamme avant d’en recevoir la moindre
   * pièce. Les exiger ferait saisir des zéros, ou pire, inventer un stock pour passer l’étape.
   */
  qte: Record<string, string>;
  /** Étape 5 — où vont ces quantités. Seule l’entrée directe est construite. */
  dest: 'stock' | 'cmd' | 'bl';
}

/** Le brouillon d’une gamme neuve — exporté pour que les bancs partent du même état que l’écran. */
export const VIDE: BrouillonGamme = {
  nom: '', code: '', secteur: '', famille: '', brand: '', supplier: '', saison: '', tva: '2000', web: false,
  grid: '', grid2: '', droppedSizes: {}, couleurs: [],
  bcMode: 'auto', codes: {}, casesExclues: {},
  corner: false, pa: '', coeff: '', pvBase: '', prixVariable: false, pvParCase: {},
  qte: {}, dest: 'stock',
};

const TVA = [
  { value: '2000', label: '20,00 %' }, { value: '1000', label: '10,00 %' },
  { value: '550', label: '5,50 %' }, { value: '210', label: '2,10 %' }, { value: '0', label: '0,00 %' },
];

/** Ce qui manque pour franchir chaque étape — un bouton inactif sans motif fait chercher à l’aveugle. */
const BLOCK_REASON: Record<number, Translatable> = {
  1: key('Désignation, famille et TVA sont nécessaires pour continuer.'),
  2: key('Il faut au moins une taille retenue ET une couleur : sans ça, la matrice est vide.'),
  3: key('Aucune case ne porte de code-barres : aucune déclinaison ne serait créée.'),
  4: key('Un prix de vente est nécessaire — le prix d’achat, lui, reste facultatif.'),
};

export function AssistantGamme({ open, onClose, onCree, offres }: {
  open: boolean;
  onClose: () => void;
  /** La gamme a été écrite — la liste doit se relire, sinon elle affiche un catalogue périmé. */
  onCree: () => void;
  offres: ArticleFacets | null;
}) {
  const [etape, setEtape] = useState(1);
  const [d, setD] = useState<BrouillonGamme>(VIDE);
  /** Les grilles du magasin — leur PROPRE route : une grille n’est pas une facette. */
  const [grids, setGrids] = useState<SizeGridOption[]>([]);
  /** Les grilles SECONDAIRES du magasin — la liste dans laquelle le second axe se choisit. */
  const [dimensions, setDimensions] = useState<SecondAxisOption[]>([]);
  /**
   * Le plafond de déclinaisons par création, tel que le SERVEUR l'annonce — jamais une constante
   * recopiée ici, qui divergerait le jour où il change.
   * ⚠️ `null` = pas encore connu, et non « pas de limite » : il arrive avec les grilles, or sans
   * grille il n'y a pas de taille, donc pas de matrice. L'inconnu est ici hors d'atteinte.
   */
  const [maxVariants, setMaxVariants] = useState<number | null>(null);
  /** Écriture en cours — une modale ne se ferme PAS pendant : on ne saurait pas dire ce qui existe. */
  const [writing, setWriting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // Chaque ouverture repart d’un brouillon neuf, à la première étape : rouvrir sur l’étape 4 du
  // précédent ferait créer une gamme dont personne n’a relu l’identité.
  useEffect(() => { if (open) { setD(VIDE); setEtape(1); setFailure(null); } }, [open]);

  useEffect(() => {
    if (!open) return;
    let vivant = true;
    api.articleSizeGrids()
      .then((r) => { if (vivant) { setGrids(r.grids); setDimensions(r.dimensions); setMaxVariants(r.maxVariants); } })
      // ⚠️ Best-effort ASSUMÉ : sans grille l’étape 2 le DIT (liste vide, « À choisir »), elle ne
      // prétend pas en avoir. Un échec de lecture ne doit pas fermer l’assistant en cours de route.
      .catch(() => { if (vivant) { setGrids([]); setDimensions([]); } });
    return () => { vivant = false; };
  }, [open]);

  const set = useCallback(
    <K extends keyof BrouillonGamme>(k: K, v: BrouillonGamme[K]) => setD((p) => ({ ...p, [k]: v })),
    [],
  );
  const { offered, createAndOffer } = useOfferedFacets(offres);

  /** Les tailles retenues — la grille moins ce qui a été écarté à l’étape 2. */
  const keptSizes = useMemo(() => {
    const toutes = grids.find((g) => g.name === d.grid)?.sizes ?? [];
    return toutes.filter((z) => !d.droppedSizes[z]);
  }, [grids, d.grid, d.droppedSizes]);
  /** Le second axe CHOISI à l'étape 2 — `null` tant qu'aucun ne l'est. */
  const gridDimension = useMemo(() => dimensions.find((x) => x.name === d.grid2) ?? null, [dimensions, d.grid2]);
  /**
   * LES COLONNES DE LA MATRICE : les tailles retenues, croisées avec la dimension de la grille.
   * ⚠️ C'est ici que douze tailles deviennent trente-six colonnes : le compte à créer, le plafond et
   * l'allocation des gencodes en découlent tous — un seul endroit les compose.
   */
  const axes = useMemo(() => sizeAxes(keptSizes, gridDimension), [keptSizes, gridDimension]);

  /**
   * LE CODE ARTICLE PROPOSÉ — dérivé de la désignation, modifiable (« Proposé, modifiable »).
   * ⚠️ Proposé seulement : l'imposer empêcherait de rattacher une gamme à un article existant, ce que
   * l'écran annonce explicitement (« la gamme sera rattachée à l'article existant »).
   */
  // ⚠️ LA RÈGLE EST CELLE DU CŒUR (`proposeArticleCode`), partagée avec l'import de fichier : la même désignation
  //    doit donner le même code partout, sinon une déclinaison importée ne rejoint plus son modèle.
  const codePropose = useMemo(() => proposeArticleCode(d.nom), [d.nom]);

  /**
   * COMBIEN DE DÉCLINAISONS CETTE GAMME CRÉERAIT — le compte que l'encart annonce ET que le plafond
   * juge. ⚠️ UN SEUL, délibérément : l'encart en affichait un (toutes les cases) pendant que le refus
   * en jugeait un autre (les non exclues), sous le même mot « déclinaisons » (revue 2026-08-25).
   */
  const toCreate = useMemo(
    () => variantsToCreate(casesDeLaMatrice(d.couleurs, axes), d.codes, d.casesExclues, d.bcMode !== 'sup'),
    [d.couleurs, axes, d.codes, d.casesExclues, d.bcMode],
  );

  /** Le motif qui interdit la matrice à l'étape de COMPOSITION — `null` quand elle est créable. */
  const horsBornes = useMemo(
    () => (maxVariants === null ? null : matriceHorsBornes(toCreate, maxVariants, 'composition')),
    [toCreate, maxVariants],
  );

  // ⚠️ CHAQUE ÉTAPE A SA PROPRE CONDITION DE PASSAGE. Un « Suivant » toujours actif laisse arriver au
  // récapitulatif avec un article qu'on ne peut pas créer — et le refus tombe alors cinq écrans trop tard.
  const canAdvance = useCallback((n: number): boolean => {
    if (n === 1) return d.nom.trim() !== '' && d.famille !== '' && d.tva !== '';
    // ⚠️ UNE MATRICE VIDE NE MÈNE NULLE PART : sans taille retenue ni couleur, l’étape des
    // codes-barres n’aurait aucune case à remplir. Le refus tombe ici, pas trois écrans plus loin.
    if (n === 2) {
      // ⚠️ ET PAS PLUS QUE CE QUE LA CRÉATION ACCEPTE (correctif d'audit 2026-08-25) : la matrice se
      //    compose ICI, c'est donc ici que sa taille se juge. Laisser passer 120 cases faisait saisir
      //    codes, prix et quantités pour rien — et brûlait 120 codes-barres au passage.
      if (horsBornes !== null) return false;
      return keptSizes.length > 0 && d.couleurs.length > 0;
    }
    // ⚠️ AU MOINS UNE CASE CODEE : sans code, aucune déclinaison ne sera créée, et les étapes prix
    // et quantités porteraient sur un ensemble vide.
    if (n === 3) return Object.values(d.codes).some((c) => c.trim() !== '');
    // ⚠️ UN PRIX DE VENTE RÉEL : le prix d’achat, lui, reste facultatif — la note de l’étape dit ce
    // qu’on perd sans lui (marge et coefficient « N/C »), et un article est vendable sans.
    if (n === 4) {
      const pv = parseMinor(d.pvBase, { scale: 2 });
      return pv.state === 'ok' && pv.minor > 0;
    }
    // ⚠️ TOUJOURS FRANCHISSABLE : les quantités sont facultatives, et la seule destination ouverte
    // est choisie par défaut. Bloquer ici ferait saisir un stock pour avancer.
    if (n === 5) return true;
    // L’étape 6 est la DERNIÈRE : on n’en sort pas en avançant, on en sort en CRÉANT.
    return false; // reliquat : rien ne doit laisser croire qu'on peut les franchir
  }, [d, keptSizes, horsBornes]);

  const create = useCallback(async () => {
    setWriting(true);
    setFailure(null);
    try {
      await createTheRange(d, casesDeLaMatrice(d.couleurs, axes), d.code.trim() || codePropose);
      onCree();
      onClose();
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « code-barres déjà pris » et « famille inconnue » appellent deux gestes
      // différents. Et si l’échec survient APRÈS les déclinaisons, l’article EXISTE — le dire évite
      // qu’on recrée une gamme en double.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setWriting(false);
    }
  }, [d, axes, codePropose, onCree, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      // 880 px au relevé — `xxl` (880) tombe juste.
      width="xxl"
      title={t('Nouvelle gamme')}
      subtitle={t('Toutes les tailles et couleurs, codes-barres et quantités')}
      icon="grid"
      tone="violet"
      blockClose={() => writing}
      // ⚠️ DENSITÉ `ample`, LA VALEUR PAR DÉFAUT — et surtout PAS `fiche`. Les deux existent, et
      //    `fiche` est celle de la FICHE article : tuile de 52 px, titre de 19. Le relevé de cette
      //    modale-ci donne 38 et 16,5 — exactement `ample`. C’est ce qui décalait tout l’en-tête.
      footer={
        <>
          {etape > 1 && (
            <button type="button" className="btn-ghost" onClick={() => setEtape((n) => n - 1)}>{t('Précédent')}</button>
          )}
          <div style={{ flex: 1 }} />
          {/* ⚠️ « Annuler » EST NU (relevé l.2320) : dans un assistant, il abandonne un travail
              commencé — lui donner l'apparence d'un bouton l'aligne visuellement sur l'action qu'on
              veut, alors qu'il fait exactement l'inverse. */}
          <button
            type="button" onClick={onClose}
            style={{
              height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-ctrl)',
              background: 'transparent', color: 'var(--ink-500)', fontSize: '13.5px',
              fontWeight: 600, cursor: 'pointer',
            }}
          >{t('Annuler')}</button>
          {/* ⚠️ À LA DERNIÈRE ÉTAPE, LE BOUTON CHANGE DE NATURE : « Suivant » avance, « Créer la gamme »
              ÉCRIT. Garder le même libellé jusqu'au bout ferait déclencher une écriture en croyant
              tourner une page. */}
          {etape < ETAPES.length ? (
            <button
              type="button" className="btn-cta" aria-disabled={!canAdvance(etape) || undefined}
              title={canAdvance(etape) ? undefined : (etape === 2 && horsBornes) || (BLOCK_REASON[etape] ? t(BLOCK_REASON[etape]) : undefined)}
              onClick={() => { if (canAdvance(etape)) setEtape((n) => n + 1); }}
            >{t('Suivant')}</button>
          ) : (
            <button
              type="button" className="btn-cta" aria-disabled={writing || undefined}
              onClick={() => { if (!writing) void create(); }}
            >{writing ? t('Création…') : t('Créer la gamme')}</button>
          )}
        </>
      }
    >
      {/* ⚠️ L’ÉCHEC RESTE À L’ÉCRAN, avec son motif réel. Un toast disparaît, et l’opérateur ne
          saurait plus si la gamme existe — il en recréerait une seconde. */}
      {failure !== null && (
        <div style={{
          margin: '0 0 14px', padding: '13px 15px', borderRadius: 'var(--r-sm)',
          background: 'var(--rose-bg)', border: '1px solid var(--rose-fg)', color: 'var(--rose-fg)',
          fontSize: '12.5px', lineHeight: 1.5,
        }} role="alert">
          
          {t('La création s’est arrêtée : {failure}', { failure })}
        </div>
      )}

      {/* ⚠️ FIL DU SOCLE : l’assistant d’import porte le MÊME au relevé (même pastille de 22 px, même
          trait). Deux copies auraient divergé à la première retouche. */}
      <Steps items={ETAPES.map((nom) => t(nom))} current={etape} onGo={setEtape} label={t('Étapes de la création d’une gamme')} />

      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>
          {HEADINGS[etape] && t(HEADINGS[etape].heading)}
        </div>
        {HEADINGS[etape]?.sous && (
          <p style={{ margin: '0 0 14px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t(HEADINGS[etape].sous)}</p>
        )}

        {etape === 1 && (
          <Identite d={d} set={set} codePropose={codePropose} offres={offered} onCreateValue={createAndOffer} />
        )}

        {etape === 2 && (
          <SizesAndColors d={d} set={set} grids={grids} dimensions={dimensions} offres={offered} horsBornes={horsBornes} toCreate={toCreate} />
        )}

        {etape === 3 && (
          <CodesBarresGamme d={d} set={set} couleurs={d.couleurs} axes={axes} max={maxVariants} toCreate={toCreate} />
        )}

        {etape === 4 && (
          <PrixGamme d={d} set={set} cases={casesDeLaMatrice(d.couleurs, axes)} />
        )}

        {etape === 5 && (
          <QuantitesGamme d={d} set={set} couleurs={d.couleurs} axes={axes} />
        )}

        {etape === 6 && (
          <RecapGamme d={d} cases={casesDeLaMatrice(d.couleurs, axes)} codePropose={codePropose} />
        )}
      </div>
    </Modal>
  );
}


/**
 * UNE CARTE D'ÉTAPE — en-tête à tuile + compteur, corps à part (relevé : `--r-md`, bordée, `overflow`).
 *
 * ⚠️ LE COMPTEUR EST DANS L'EN-TÊTE, en chiffres tabulaires : « 5 tailles retenues sur 8 » se lit sans
 * recompter les pastilles, et c'est la seule information qui dit si l'étape est faite.
 */
function CarteEtape({ icone, heading, compteur, children }: {
  icone: React.ReactNode;
  heading: string;
  compteur: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--ink-200)',
      borderRadius: 'var(--r-md)', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{
          width: 27, height: 27, borderRadius: 'var(--r-xs)', background: 'var(--teal-100)',
          color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>{icone}</span>
        <span style={{ flex: 1, fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)',
          fontVariantNumeric: 'tabular-nums',
        }}>{compteur}</span>
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

/** Le mètre de couturier — tuile de la carte Tailles. */
function SizesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="9" width="18" height="6" rx="1.5" />
      <path d="M7 9v3M11 9v3M15 9v3" />
    </svg>
  );
}

/** La palette — tuile de la carte Couleurs. */
function IconeCouleurs() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="13.5" cy="6.5" r="1" />
      <circle cx="17.5" cy="10.5" r="1" />
      <circle cx="8.5" cy="7.5" r="1" />
      <circle cx="6.5" cy="12.5" r="1" />
      <path d="M12 2a10 10 0 0 0 0 20 2 2 0 0 0 2-2v-1a2 2 0 0 1 2-2h1a5 5 0 0 0 5-5c0-5.5-4.5-10-10-10z" />
    </svg>
  );
}

/**
 * ÉTAPE 2 — LA MATRICE SE DÉFINIT ICI : la grille donne les colonnes, les couleurs donnent les lignes.
 *
 * ⚠️ TOUTES LES TAILLES SONT RETENUES PAR DÉFAUT, et on ÉCARTE. L'inverse — cocher ce qu'on veut —
 * obligerait à huit clics pour le cas courant (« je prends toute la grille ») et à zéro pour le cas
 * rare. C'est aussi pourquoi l'état vit en « tailles écartées » plutôt qu'en « tailles retenues » :
 * changer de grille ne doit pas vider une sélection que personne n'a faite.
 *
 * ⚠️ LES COULEURS NE SONT PAS UN RÉFÉRENTIEL. Il n'existe aucune table `color` : le coloris est du
 * texte porté par la déclinaison. Elles se lisent donc comme la marque — l'ensemble des valeurs du
 * catalogue — et une couleur neuve devient réelle quand la première déclinaison qui la porte s'écrit.
 * D'où la garde anti-quasi-doublon du picker : « NAVY » et « Navy » éclateraient stocks et statistiques.
 */
function SizesAndColors({ d, set, grids, dimensions, offres, horsBornes, toCreate }: {
  d: BrouillonGamme;
  set: <K extends keyof BrouillonGamme>(k: K, v: BrouillonGamme[K]) => void;
  grids: SizeGridOption[];
  dimensions: SecondAxisOption[];
  offres: ArticleFacets | null;
  /** Le motif qui rend la matrice incréable, ou `null` — il remplace le compte dans l'encart. */
  horsBornes: string | null;
  /** Le nombre de déclinaisons que la création recevrait — le MÊME que celui que le plafond juge. */
  toCreate: number;
}) {
  const allSizes = useMemo(
    () => (d.grid ? grids.find((g) => g.name === d.grid)?.sizes ?? [] : []),
    [d.grid, grids],
  );
  const retenues = useMemo(
    () => allSizes.filter((z) => !d.droppedSizes[z]),
    [allSizes, d.droppedSizes],
  );
  /** Le second axe CHOISI ici — la maquette l'a toujours dessiné comme un choix d'article. */
  const dimension = useMemo(
    () => (d.grid2 ? dimensions.find((x) => x.name === d.grid2) ?? null : null),
    [d.grid2, dimensions],
  );

  const basculer = (z: string) => {
    const suite = { ...d.droppedSizes };
    if (suite[z]) delete suite[z]; else suite[z] = true;
    set('droppedSizes', suite);
  };

  // Le mot du second axe est celui du MAGASIN : il ne se met pas au pluriel par un « s » — il se nomme. Deux phrases
  // entières, choisies selon que le second axe existe.
  const sizeCount = !d.grid ? ''
    : dimension && dimension.values.length > 0
      ? t('{n} taille retenue sur {total} × {v} ({champ})', { n: retenues.length, total: allSizes.length, v: dimension.values.length, champ: dimension.label.toLowerCase() })
      : t('{n} taille retenue sur {total}', { n: retenues.length, total: allSizes.length });
  const compteurCouleurs = d.couleurs.length
    ? t('{n} couleur', { n: d.couleurs.length })
    : t('aucune couleur');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 2 }}>
      <CarteEtape icone={<SizesIcon />} heading={t('Tailles')} compteur={sizeCount}>
        <div style={{ maxWidth: 460 }}>
          <Picker
            id="bo-wz-grille" variant="field" label={fieldLabel('sizeGrid')}
            clearLabel={t('À choisir')} allLabel={t('À choisir')}
            options={grids.map((g) => ({
              value: g.name,
              label: g.name,
              // La grille se choisit sur SON CONTENU : « Vêtements FR » ne dit pas s'il y a du XXL.
              hint: g.sizes.join(' · '),
            }))}
            value={d.grid}
            // ⚠️ VIDER LA GRILLE VIDE LE SECOND AXE : gardé en mémoire, il croiserait une grille qui n'est plus là.
            onChange={(v) => { set('grid', v ?? ''); set('droppedSizes', {}); if (!v) set('grid2', ''); }}
          />
        </div>

        {/* ⚠️ LE SECOND AXE SE CHOISIT ICI, ET C'EST CE QUE LA MAQUETTE DESSINAIT (« Longueurs —
            facultatif · dimension 2 »), TOUJOURS AFFICHÉ sous la grille (décision de l'utilisateur) : on voit
            d'emblée tout ce que la gamme peut être. ⚠️ INACTIF TANT QU'AUCUNE GRILLE N'EST CHOISIE, et il le
            dit : sans tailles, rien à croiser. */}
        <div style={{ maxWidth: 460, marginTop: 11 }}>
          <Picker
            id="bo-wz-grille2" variant="field" label={t('{v} — facultatif', { v: fieldLabel('sizeGrid2') })}
            clearLabel={t('Sans · un seul axe')} allLabel={t('Sans · un seul axe')}
            options={dimensions.map((x) => ({ value: x.name, label: x.label, hint: x.values.join(' · ') }))}
            unavailable={d.grid === '' || dimensions.length === 0}
            unavailableReason={d.grid === ''
              ? t('Choisissez d’abord la grille de tailles : le second axe se croise avec elle.')
              : t('Aucune grille de second axe au référentiel de ce magasin — créez-la au référentiel des grilles.')}
            value={d.grid2}
            onChange={(v) => set('grid2', v ?? '')}
          />
        </div>

        {/* ⚠️ UNE GRILLE SANS TAILLE BLOQUE L’ÉTAPE, et le refus doit se comprendre. Sans ce mot,
            « Suivant » restait inactif devant une liste de tailles vide, sans que rien ne dise que
            c’est la grille qui est à compléter — et pas l’écran qui est cassé. */}
        {d.grid !== '' && allSizes.length === 0 && (
          <div style={{
            marginTop: 13, padding: '11px 13px', borderRadius: 'var(--r-ctrl)',
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
            fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)', lineHeight: 1.5,
          }}>
            
            {t('Cette grille n’a encore aucune taille — une gamme a besoin d’au moins une colonne. Ajoutez-lui ses tailles dans Référentiels › Grilles de tailles.')}
          </div>
        )}

        {d.grid !== '' && allSizes.length > 0 && (
          <div style={{ marginTop: 13 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 9,
            }}>
              <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)' }}>
                
                {t('Tout est retenu par défaut — cliquez une taille pour l’écarter ou la reprendre.')}
              </span>
              <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
                <BoutonMini onClick={() => set('droppedSizes', {})}>{t('Toutes')}</BoutonMini>
                <BoutonMini onClick={() => set('droppedSizes', Object.fromEntries(allSizes.map((z) => [z, true as const])) as Record<string, true>)}>
                  
                  {t('Aucune')}
                </BoutonMini>
              </span>
            </div>

            <div style={{
              display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 136, overflowY: 'auto', padding: 2,
            }}>
              {allSizes.map((z) => {
                const on = !d.droppedSizes[z];
                return (
                  <button
                    key={z} type="button" onClick={() => basculer(z)}
                    aria-pressed={on}
                    style={{
                      height: 32, minWidth: 50, padding: '0 13px', borderRadius: 'var(--r-xs)',
                      border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                      background: on ? 'var(--teal-100)' : 'var(--paper)',
                      color: on ? 'var(--teal-800)' : 'var(--ink-400)',
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 700,
                      // ⚠️ LA TAILLE ÉCARTÉE RESTE LISIBLE, BARRÉE : la retirer de la liste ferait
                      // croire que la grille n'en contient pas, et il n'y aurait plus rien à recliquer.
                      textDecoration: on ? 'none' : 'line-through',
                      cursor: 'pointer',
                    }}
                  >{z}</button>
                );
              })}
            </div>

            {/* Ce que le second axe choisi fait à la matrice — combien de colonnes en sortent. */}
            {dimension && (
              <div style={{ marginTop: 11, fontSize: '11.5px', color: 'var(--violet-fg)', lineHeight: 1.5 }}>
                {/* ⚠️ DEUX PHRASES ENTIÈRES : la conséquence sur la matrice passait en VALEUR, un morceau de phrase que le
                    traducteur recevait sans son début. */}
                {dimension.values.length > 0 ? (
                  <Trans
                    k="<1>{axe}</1> · dimension 2 : {valeurs} — chaque taille retenue se décline dans chaque {champ} ({tailles} × {nb} = {n} colonnes)."
                    vars={{ axe: dimension.label, valeurs: dimension.values.join(' · '), champ: dimension.label.toLowerCase(), tailles: retenues.length, nb: dimension.values.length, n: retenues.length * dimension.values.length }}
                    parts={[(x) => <strong>{x}</strong>]}
                  />
                ) : (
                  <Trans
                    k="<1>{axe}</1> · dimension 2 : {valeurs}"
                    vars={{ axe: dimension.label, valeurs: t('aucune valeur déclarée — un seul axe tant qu’elle n’en a pas') }}
                    parts={[(x) => <strong>{x}</strong>]}
                  />
                )}
              </div>
            )}
            {retenues.length === 0 && (
              <div style={{
                marginTop: 9, padding: '9px 12px', borderRadius: 'var(--r-ctrl)',
                background: 'var(--rose-bg)', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--rose-fg)',
              }}>
                
                {t('Aucune taille retenue — gardez-en au moins une pour passer aux codes-barres.')}
              </div>
            )}
          </div>
        )}
      </CarteEtape>

      <CarteEtape icone={<IconeCouleurs />} heading={t('Couleurs')} compteur={compteurCouleurs}>
        <div style={{ maxWidth: 460 }}>
          <Picker
            id="bo-wz-couleurs" variant="field" multiple
            label={t('{v} — chaque valeur cochée ajoute une ligne à la matrice', { v: fieldLabel('color') })}
            clearLabel={t('À choisir')} allLabel={t('À choisir')}
            options={(offres?.values.coul ?? []).map((v) => ({ value: v.value, label: v.value }))}
            value={d.couleurs}
            onChange={(v) => set('couleurs', v)}
            // ⚠️ CHERCHE AU-DELÀ des valeurs reçues : la liste des coloris d'un magasin dépasse
            // largement le plafond de facette, et sans elle on conclurait qu'une couleur n'existe pas.
            onSearch={(q) => api.articleFacetSearch('coul', q).then((r) => r.map((v) => ({ value: v.value, label: v.value })))}
            // ⚠️ « Créer » N'ÉCRIT NULLE PART, et c'est exact : un coloris n'a pas de table. Il est
            // simplement retenu pour la matrice, et devient réel quand ses déclinaisons sont écrites.
            // Le picker garde en revanche sa garde anti-quasi-doublon, qui est tout l'enjeu ici.
            onCreate={(valeur) => Promise.resolve(valeur.trim())}
            createLabel={t('Utiliser')}
          />
        </div>

        {d.couleurs.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {d.couleurs.map((c) => (
              <span key={c} style={{
                height: 30, padding: '0 6px 0 12px', borderRadius: 'var(--r-pill)',
                background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '12.5px',
                fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
                {c}
                <button
                  type="button" aria-label={t('Retirer {c}', { c })}
                  onClick={() => set('couleurs', d.couleurs.filter((x) => x !== c))}
                  style={{
                    width: 20, height: 20, border: 'none', borderRadius: 'var(--r-pill)',
                    background: 'rgba(14,91,77,.12)', color: 'var(--teal-800)', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.4" strokeLinecap="round" aria-hidden>
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* ⚠️ LE MÊME ENCART DIT LES DEUX ISSUES — le compte, ou le refus. Annoncer « N déclinaisons
            possibles » alors que la création les refusera, c'est promettre une suite qui n'existe pas. */}
        {d.grid !== '' && d.couleurs.length > 0 && (
          <div style={{
            marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)',
            background: horsBornes ? 'var(--rose-bg)' : 'var(--ink-50)',
            border: `1px solid ${horsBornes ? 'var(--rose-fg)' : 'var(--ink-200)'}`,
            fontSize: '12.5px', color: horsBornes ? 'var(--rose-fg)' : 'var(--ink-600)', lineHeight: 1.55,
          }} role={horsBornes ? 'alert' : undefined}>
            {horsBornes ?? (
              <>
                <Trans
                  k='Cela fait <1>{toCreate}</1> déclinaisons possibles. À l’étape suivante vous choisissez lesquelles existent vraiment.'
                  vars={{ toCreate }}
                  parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span></strong>]}
                />
              </>
            )}
          </div>
        )}
      </CarteEtape>
    </div>
  );
}

/**
 * ÉTAPE 3 — CHAQUE CASE DE LA MATRICE REÇOIT SON CODE-BARRES, d'un côté ou de l'autre.
 *
 * ⚠️ L'ALLOCATION EST RÉELLE ET SE FAIT À L'ARRIVÉE, en mode automatique. La phrase du relevé AFFIRME
 * que « chaque case a reçu son gencode » : afficher une matrice vide sous cette phrase serait un écran
 * qui ment (§23.0). Le prix est connu et assumé — traverser l'étape puis abandonner brûle des numéros,
 * la séquence étant monotone et non sans trou.
 *
 * ⚠️ UNE CASE SANS CODE N'EST PAS CRÉÉE, et c'est le seul moyen d'exclure une exception (le 38 qui
 * n'existe pas en rouge). Cliquer une case pleine RETIRE son code ; recliquer en alloue un neuf.
 *
 * ⚠️ LES DEUX MODES SONT EXCLUSIFS. Passer au scan efface les codes maison : les laisser afficherait
 * des codes système dans des cases qui attendent la douchette. On DEMANDE avant, parce que le geste
 * détruit aussi les codes fournisseur déjà lus.
 */
function CodesBarresGamme({ d, set, couleurs, axes, max, toCreate }: {
  d: BrouillonGamme;
  set: <K extends keyof BrouillonGamme>(k: K, v: BrouillonGamme[K]) => void;
  couleurs: readonly string[];
  /** Les colonnes — tailles croisées avec la dimension de la grille ; leur libellé fait la colonne. */
  axes: readonly SizeAxis[];
  /** Plafond servi par le serveur — `null` tant qu'il n'est pas connu (aucune matrice ne l'est alors). */
  max: number | null;
  /** Le nombre de déclinaisons que la création recevrait, mode compris. */
  toCreate: number;
}) {
  const [liveCount, setEnCours] = useState(false);
  /**
   * LE MOTIF DU DERNIER ÉCHEC D’ALLOCATION — affiché, et surtout BLOQUANT pour la reprise auto.
   * ⚠️ SANS LUI, LA BOUCLE : un échec ne remplit aucune case, donc l’effet reverrait exactement les
   * mêmes cases manquantes au rendu suivant et rappellerait le serveur, indéfiniment. On s’arrête au
   * premier refus, on l’affiche, et c’est un geste de l’utilisateur qui réarme.
   */
  const [failure, setFailure] = useState<string | null>(null);
  const auto = d.bcMode !== 'sup';

  const cases = useMemo(() => casesDeLaMatrice(couleurs, axes), [couleurs, axes]);
  /** Ce que la matrice et la liste de scan AFFICHENT en colonne : le libellé (« 40/32 » sur deux axes). */
  const sizes = useMemo(() => axes.map((a) => a.label), [axes]);
  /** Le refus, formulé pour CETTE étape : ici exclure une case est un geste réellement offert. */
  const horsBornes = max === null ? null : matriceHorsBornes(toCreate, max, 'codes');
  const retenues = cases.filter((x) => (d.codes[x.k] ?? '') !== '').length;

  /**
   * LA PORTE DE L'ALLOCATION — une réponse n'écrit que le brouillon qui l'a demandée.
   * ⚠️ LES CARTES DE MODE RESTENT CLIQUABLES PENDANT L'ALLOCATION : passer en « Gencodes fournisseur » vide les codes,
   *    et la réponse revenue ensuite remplissait la liste de scan de codes MAISON sous la phrase « le code du
   *    fournisseur fait foi ». Changer de mode, quitter l'étape ou fermer l'assistant la rendent caduque.
   * ⚠️ UNE ALLOCATION CADUQUE EST PERDUE, et c'est voulu : le brouillon ne porte aucune identité, donc rien ne
   *    distingue l'étape 3 qu'on retrouve de celle d'un AUTRE article ouvert entre-temps — des codes de réserve
   *    sautés valent mieux que des codes posés sur la mauvaise gamme. L'effet réalloue au retour.
   */
  const latestAllocation = useLatest();
  useEffect(() => () => { latestAllocation(); }, [latestAllocation]);

  /** Alloue un vrai lot pour les cases nommées, et ne pose rien s'il revient incomplet. */
  const allouer = useCallback(async (targets: readonly string[]) => {
    if (targets.length === 0) return;
    const isLatest = latestAllocation();
    setEnCours(true);
    try {
      const { gencodes } = await api.allocateGencodes(targets.length);
      if (!isLatest()) return;
      if (gencodes.length !== targets.length) {
        throw new Error(t('{n} codes reçus pour {demandes}.', { n: gencodes.length, demandes: t('{n} demandé', { n: targets.length }) }));
      }
      const maj = { ...d.codes };
      targets.forEach((k, i) => { maj[k] = gencodes[i]!; });
      set('codes', maj);
    } catch (e) {
      // Le refus d'une allocation caduque ne décrit plus rien de ce que l'écran montre.
      if (!isLatest()) return;
      // ⚠️ LE MOTIF RÉEL : « ce magasin n'a pas de radical EAN » se règle depuis la Console, pas en
      // recliquant. La matrice reste alors vide — ce qui est la vérité.
      const reason = e instanceof Error ? e.message : String(e);
      setFailure(reason);
      showToast(reason, 'err');
    } finally {
      // ⚠️ SEULE L'ALLOCATION À JOUR REND LA MAIN : le « fini » d'une caduque, arrivé pendant qu'une plus récente est en
      //    vol, réarmerait l'effet — qui en lancerait une troisième, rendant caduque la deuxième, et ainsi de suite.
      if (isLatest()) setEnCours(false);
    }
  }, [d.codes, set, latestAllocation]);

  // ⚠️ À L'ARRIVÉE, et à chaque agrandissement de la matrice : revenir à l'étape 2 pour ajouter un
  // coloris doit remplir les cases neuves, sinon une ligne resterait vide sous une phrase qui promet
  // le contraire. Les cases VOLONTAIREMENT exclues ne sont jamais reprises — c'est à ça que sert la
  // liste d'exclusions plutôt qu'un « souvenir » de ce que ce rendu-ci a déjà vu.
  useEffect(() => {
    if (liveCount) return;
    const manquantes = casesAAllouer({
      cases, codes: d.codes, exclues: d.casesExclues, failure, auto, max: max ?? Infinity,
    });
    if (manquantes.length > 0) void allouer(manquantes);
  }, [auto, cases, liveCount, failure, allouer, d.codes, d.casesExclues, max]);

  const basculerMode = useCallback(async (mode: 'auto' | 'sup') => {
    if (mode === d.bcMode) return;
    if (mode === 'sup' && retenues > 0) {
      const accord = await confirm({
        title: t('Effacer {n} code déjà posé ?', { n: retenues }),
        message: t('Le mode fournisseur part de cases vides : les codes maison déjà attribués — et les codes déjà scannés — seront effacés.'),
        confirmLabel: t('Passer au scan'),
      });
      if (!accord) return;
    }
    // L'allocation en vol visait l'autre mode : sa réponse ne s'écrira pas, et c'est ce geste qui rend la main.
    latestAllocation();
    setEnCours(false);
    setFailure(null);
    set('bcMode', mode);
    set('casesExclues', {});
    if (mode === 'sup') set('codes', {});
  }, [d.bcMode, retenues, set, latestAllocation]);

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>
          
          {t('Codes-barres')}
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
          {auto
            ? t('La gamme complète de l’étape précédente existe — chaque case a reçu son gencode. Cliquez une case pour exclure une exception.')
            : t('Scannez le code-barres fournisseur de chaque déclinaison — une case sans code n’est pas créée.')}
        </p>

        <div role="radiogroup" aria-label={t('Origine des gencodes')} style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10,
        }}>
          <CarteMode
            index={0} pick={(k) => void basculerMode(k === 0 ? 'auto' : 'sup')}
            choisie={auto} onClick={() => void basculerMode('auto')}
            heading={t('Gencodes automatiques')}
            aide={t('Générés par le système — la gamme est prête immédiatement.')}
          />
          <CarteMode
            index={1} pick={(k) => void basculerMode(k === 0 ? 'auto' : 'sup')}
            choisie={!auto} onClick={() => void basculerMode('sup')}
            heading={t('Gencodes fournisseur')}
            aide={t('Scannez chaque déclinaison — le code du fournisseur fait foi.')}
          />
        </div>
      </div>

      {/* ⚠️ LE PLAFOND SE DIT ICI AUSSI (revue 2026-08-25). L'allocation s'arrête au-delà, et sans cet
          encart ré-inclure une case exclue ne faisait RIEN — en silence, sous une phrase qui promet que
          chaque case reçoit son gencode. Le geste proposé est celui de CETTE étape. */}
      {horsBornes !== null && (
        <div style={{
          marginBottom: 12, padding: '11px 13px', borderRadius: 'var(--r-sm)',
          background: 'var(--rose-bg)', border: '1px solid var(--rose-fg)', color: 'var(--rose-fg)',
          fontSize: '12.5px', lineHeight: 1.5,
        }} role="alert">
          {horsBornes}
        </div>
      )}

      {failure !== null && (
        <div style={{
          marginBottom: 12, padding: '11px 13px', borderRadius: 'var(--r-sm)',
          background: 'var(--rose-bg)', border: '1px solid var(--rose-fg)', color: 'var(--rose-fg)',
          fontSize: '12.5px', lineHeight: 1.5,
        }} role="alert">
          
          {t('Aucun code n’a pu être attribué : {failure}', { failure })}
        </div>
      )}

      {auto
        ? <MatriceCodes
            couleurs={couleurs} sizes={sizes} codes={d.codes} liveCount={liveCount}
            onCase={(k) => {
              if (d.codes[k]) {
                const maj = { ...d.codes };
                delete maj[k];
                set('codes', maj);
                set('casesExclues', { ...d.casesExclues, [k]: true });
              } else {
                // Reprendre une case exclue suffit à la faire réallouer par l'effet ci-dessus — et ce
                // geste explicite réarme la reprise auto qu'un échec avait arrêtée.
                setFailure(null);
                const reste = { ...d.casesExclues };
                delete reste[k];
                set('casesExclues', reste);
              }
            }}
          />
        : <ListeScan
            couleurs={couleurs} sizes={sizes} codes={d.codes}
            onCode={(k, v) => set('codes', { ...d.codes, [k]: v })}
          />}

      <div style={{ display: 'flex', gap: 18, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-700)' }}>
          <Trans k='<1>{n}</1> déclinaison créée' vars={{ n: retenues }} parts={[(x) => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} /></span>
        <span style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
          <Trans k='<1>{n}</1> laissée de côté' vars={{ n: cases.length - retenues }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} />
        </span>
        <div style={{ flex: 1 }} />
        {!auto && (
          <button
            type="button"
            // ⚠️ Il POSE LE CURSEUR sur la première case vide, rien de plus : l'hôte de navigation
            // enchaîne ensuite les champs au retour chariot de la douchette.
            onClick={() => document.querySelector<HTMLInputElement>('[data-scancell]:placeholder-shown')?.focus()}
            style={{
              height: 32, padding: '0 12px', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px',
              fontWeight: 600, whiteSpace: 'nowrap', border: '1px solid var(--ink-200)',
              background: 'var(--paper)', color: 'var(--ink-700)', display: 'inline-flex',
              alignItems: 'center', gap: 6, cursor: 'pointer',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" aria-hidden>
              <path d="M3 5v14M6 5v14M9 5v14M13 5v14M17 5v14M20 5v14" />
            </svg>
            
            {t('Saisir au scan')}
          </button>
        )}
      </div>

      <div style={{
        marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
        border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55,
      }}>
        {/* ⚠️ DIVERGENCE À LA MAQUETTE, ET C'EST UNE CORRECTION : elle annonce « préfixe 20 ». Notre
            composition est `2` + le radical à cinq chiffres du magasin, donc « 20 » n'est vrai que pour
            les radicaux commençant par zéro. Un préfixe faux imprimé à l'écran est exactement ce que
            §23.0 interdit — on nomme la plage, qui est vraie partout. */}
        <Trans k='Les codes générés utilisent la plage interne du magasin (EAN-13, plage GS1 d’usage interne — préfixe <1>2</1> suivi du radical du magasin). Un code scanné du fournisseur est conservé tel quel — vous pouvez mélanger les deux dans la même gamme.' parts={[(x) => <span style={{ fontFamily: 'var(--font-mono)' }}>{x}</span>]} /></div>
    </div>
  );
}

/** Une des deux façons d'obtenir les codes — carte à pastille, pas une case à cocher. */
function CarteMode({ index, pick, choisie, heading, aide, onClick }: {
  /** Son rang dans le groupe, et le choix par rang — les flèches du groupe passent par lui. */
  index: number; pick: (index: number) => void;
  choisie: boolean; heading: string; aide: string; onClick: () => void;
}) {
  return (
    <button
      type="button" role="radio" aria-checked={choisie} onClick={onClick}
      {...rovingRadio({ index, count: 2, checked: choisie, anyChecked: true, onPick: pick })}
      className={OZ_INTERACTIVE_SURFACE}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 11, padding: '12px 14px', textAlign: 'left',
        border: `1.5px solid ${choisie ? 'var(--teal-500)' : 'var(--ink-200)'}`,
        borderRadius: 'var(--r-sm)', background: 'var(--paper)', cursor: 'pointer',
      }}
    >
      <span style={{
        width: 17, height: 17, borderRadius: 'var(--r-pill)', flexShrink: 0, marginTop: 1,
        border: `1.5px solid ${choisie ? 'var(--teal-500)' : 'var(--ink-300)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {choisie && (
          <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>
          {heading}
        </span>
        <span style={{
          display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45,
        }}>{aide}</span>
      </span>
    </button>
  );
}

/**
 * LA MATRICE couleur × taille — une case par déclinaison possible.
 * ⚠️ ELLE DÉFILE HORIZONTALEMENT DANS SON PROPRE CADRE : douze tailles ne doivent pas faire glisser
 * la modale entière, où les boutons d'étape sortiraient de l'écran.
 */
function MatriceCodes({ couleurs, sizes, codes, liveCount, onCase }: {
  couleurs: readonly string[];
  sizes: readonly string[];
  codes: Record<string, string>;
  liveCount: boolean;
  onCase: (k: string) => void;
}) {
  return (
    <div style={{ overflowX: 'auto', opacity: liveCount ? 0.6 : 1 }}>
      <div style={{ display: 'inline-block', minWidth: '100%' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <div style={{ width: 128, flexShrink: 0 }} />
          {sizes.map((s) => (
            <div key={s} style={{
              width: 82, flexShrink: 0, textAlign: 'center', fontFamily: 'var(--font-mono)',
              fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-500)',
            }}>{s}</div>
          ))}
        </div>

        {couleurs.map((c) => (
          <div key={c} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <div style={{
              width: 128, flexShrink: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }} title={c}>{c}</div>
            {sizes.map((s) => {
              const k = cleCase(c, s);
              const code = codes[k] ?? '';
              const on = code !== '';
              return (
                <button
                  key={k} type="button" onClick={() => onCase(k)} disabled={liveCount}
                  aria-pressed={on}
                  aria-label={on ? t('{couleur} {taille} — {code}', { couleur: c, taille: s, code }) : t('{couleur} {taille} — non créée', { couleur: c, taille: s })}
                  style={{
                    width: 82, height: 54, flexShrink: 0, borderRadius: 'var(--r-ctrl)',
                    border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                    background: on ? 'var(--teal-100)' : 'var(--paper)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 3, cursor: liveCount ? 'progress' : 'pointer',
                  }}
                >
                  {on ? (
                    <>
                      <span style={{ color: 'var(--teal-700)', display: 'flex' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M4 12l5 5L20 6" />
                        </svg>
                      </span>
                      {/* Les cinq derniers chiffres suffisent à distinguer deux cases voisines ; les
                          treize n'entreraient pas dans 82 px, et le code complet vit dans l'étiquette. */}
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: '9.5px', color: 'var(--teal-800)',
                        letterSpacing: '.02em',
                      }}>…{code.slice(-5)}</span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--ink-300)', display: 'flex' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" aria-hidden>
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * LA LISTE DE SCAN — une ligne par déclinaison, groupée par coloris, avec l'avancement par groupe.
 * ⚠️ LE CURSEUR AVANCE À 13 CHIFFRES : une douchette qui n'envoie pas de retour chariot laisserait
 * sinon tout le lot dans la même case, et on ne s'en apercevrait qu'à la fin.
 */
function ListeScan({ couleurs, sizes, codes, onCode }: {
  couleurs: readonly string[];
  sizes: readonly string[];
  codes: Record<string, string>;
  onCode: (k: string, v: string) => void;
}) {
  const suivant = (k: string) => {
    const champs = [...document.querySelectorAll<HTMLInputElement>('[data-scancell]')];
    const i = champs.findIndex((el) => el.dataset.scancell === k);
    champs[i + 1]?.focus();
  };

  return (
    <div style={{
      marginTop: 16, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden',
    }} data-scan-zone>
      <div style={{
        display: 'grid', gridTemplateColumns: '70px minmax(0,1fr)', gap: 10,
        padding: '9px 14px 9px 33px', background: 'var(--ink-50)',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        {/* ⚠️ CLÉ PAR POSITION : deux champs surchargés vers le même mot donneraient deux clés égales. */}
        {[fieldLabel('size'), t('{champ} fournisseur', { champ: fieldLabel('gencode') })].map((mot, i) => (
          <div key={i} style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
            textTransform: 'uppercase', color: 'var(--ink-500)',
          }}>{mot}</div>
        ))}
      </div>

      <div style={{ maxHeight: 196, overflowY: 'auto', overflowX: 'hidden' }}>
        {couleurs.map((c) => {
          const faits = sizes.filter((s) => (codes[cleCase(c, s)] ?? '') !== '').length;
          const complet = faits === sizes.length && sizes.length > 0;
          return (
            <div key={c}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px',
                background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)',
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 'var(--r-pill)', flexShrink: 0,
                  background: 'var(--teal-500)',
                }} />
                <span style={{
                  flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-900)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{c}</span>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: complet ? 'var(--teal-700)' : 'var(--ink-500)',
                }}>{faits}/{sizes.length}</span>
              </div>

              {sizes.map((s) => {
                const k = cleCase(c, s);
                return (
                  <div key={k} style={{
                    display: 'grid', gridTemplateColumns: '70px minmax(0,1fr)', gap: 10,
                    padding: '6px 14px 6px 33px', borderBottom: '1px solid var(--ink-100)',
                    alignItems: 'center',
                  }}>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-700)',
                    }}>{s}</div>
                    <input
                      data-scancell={k}
                      value={codes[k] ?? ''}
                      inputMode="numeric"
                      placeholder=""
                      aria-label={`${fieldLabel('gencode')} ${c} ${s}`}
                      onChange={(e) => {
                        const v = e.target.value;
                        onCode(k, v);
                        if (v.replace(/\D/g, '').length >= 13) suivant(k);
                      }}
                      style={{
                        width: '100%', height: 32, padding: '0 10px', boxSizing: 'border-box',
                        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
                        background: 'var(--paper)', color: 'var(--ink-900)',
                        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', outline: 'none',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div style={{
        padding: '10px 14px', background: 'var(--ink-50)', fontSize: '11.5px', color: 'var(--ink-500)',
        lineHeight: 1.5,
      }}>
        
        {t('Le curseur passe à la case suivante dès 13 chiffres, ou sur Entrée. Une case laissée vide ne crée pas la déclinaison.')}
      </div>
    </div>
  );
}

/**
 * CRÉER LA GAMME — quatre écritures, dans cet ordre, et l'ordre n'est pas indifférent.
 *
 * ⚠️ LES DÉCLINAISONS D'ABORD : tout le reste s'y rattache. Les champs se posent sur un modèle qui
 * doit exister, les prix par déclinaison sur des gencodes qui doivent exister, le stock sur des
 * articles qui doivent exister. Inverser ferait écrire dans le vide, sans erreur.
 *
 * ⚠️ AUCUNE ROUTE MAISON : ce sont les MÊMES portes que la fiche article et que « Article simple ».
 * Une écriture dédiée à l'assistant finirait par oublier de journaliser, et la gamme ne descendrait
 * à aucune caisse.
 *
 * ⚠️ ON N'AVALE AUCUN ÉCHEC. Si les champs échouent après les déclinaisons, l'article existe et le
 * dire est la seule information utile : l'écran rend le motif RÉEL, et l'opérateur reprend depuis la
 * fiche plutôt que de recréer une gamme en double.
 *
 * ⚠️ UNE SEULE CLÉ DE LOT pour les quatre écritures, dérivée du modèle et de l'instant : rejouer une
 * création interrompue retombe sur les mêmes identifiants, et rien n'est écrit deux fois.
 */
export async function createTheRange(
  d: BrouillonGamme,
  cases: readonly CaseMatrice[],
  codeEffectif: string,
): Promise<void> {
  const retenues = cases.filter((x) => (d.codes[x.k] ?? '').trim() !== '');
  if (retenues.length === 0) {
    throw new Error(t('Aucune déclinaison à créer — chaque case doit porter un code-barres.'));
  }
  const lot = `gamme:${codeEffectif}:${Date.now()}`;

  // 1) LES DÉCLINAISONS, avec le code que l'étape 3 a posé sur chaque case.
  await api.createArticleVariants(
    codeEffectif,
    // ⚠️ LES DEUX AXES PARTENT SÉPARÉS — jamais le libellé « 40/32 » dans `size` : c'est le serveur qui
    //    stocke, et il stocke deux colonnes. Sans `size2`, deux longueurs d'une taille s'écraseraient.
    retenues.map((x) => ({ color: x.c, size: x.size, size2: x.size2, gencode: d.codes[x.k]!.trim() })),
    lot,
    // ⚠️ LA GRILLE PART AVEC LES DÉCLINAISONS : le serveur résout chaque taille en ligne de SA grille, et
    //    refuse une taille sans grille. La poser à l'étape 2, avec les champs, arrivait trop tard.
    d.grid || null,
    // ⚠️ ET LA GRILLE DU SECOND AXE, pour la même raison : le serveur refuse une longueur sans sa grille.
    d.grid2 || null,
  );

  // 2) LES CHAMPS COMMUNS — par la carte de différences, la MÊME route que l'édition de la fiche.
  const pv = parseMinor(d.pvBase, { scale: 2 });
  const pa = parseMinor(d.pa, { scale: 4 });
  await api.setArticleFields(codeEffectif, {
    name: d.nom.trim(),
    family: d.famille,
    ...(d.secteur ? { sector: d.secteur } : {}),
    ...(d.brand ? { brand: d.brand } : {}),
    ...(d.supplier ? { supplier: d.supplier } : {}),
    ...(d.saison ? { season: d.saison } : {}),
    ...(d.grid ? { sizeGrid: d.grid } : {}),
    ...(d.grid2 ? { sizeGrid2: d.grid2 } : {}),
    salePriceInclTaxCents: String(pv.state === 'ok' ? pv.minor : 0),
    // ⚠️ LE PRIX D'ACHAT NE PART QU'EN RÉGIME FERME : en corner il se recalcule à chaque vente depuis
    // la clause du contrat, et en poser un ici figerait une valeur que la caisse ignorera.
    ...(!d.corner && pa.state === 'ok' ? { purchasePriceExclTaxFine: String(pa.minor) } : {}),
    vatRateBp: d.tva,
    purchaseRegime: d.corner ? 'corner' : 'firm',
    isWebActive: d.web,
  }, `${lot}:champs`);

  // 3) LES PRIX PROPRES, s'il y en a — seulement ceux qui DIFFÈRENT du tarif commun.
  if (d.prixVariable) {
    const propres = retenues
      .map((x) => ({ gencode: d.codes[x.k]!.trim(), saisi: parseMinor(d.pvParCase[x.k] ?? '', { scale: 2 }) }))
      .filter((x): x is { gencode: string; saisi: { state: 'ok'; minor: number } } => x.saisi.state === 'ok')
      .map((x) => ({ gencode: x.gencode, priceCents: x.saisi.minor }));
    // ⚠️ RIEN À ÉCRIRE N'EST PAS UN APPEL VIDE : la route refuserait un lot sans ligne, et l'écran
    // annoncerait un échec là où l'opérateur n'a simplement dérogé sur aucune taille.
    if (propres.length > 0) await api.setArticleVariantPrices(codeEffectif, propres, `${lot}:prix`);
  }

  // 4) LE STOCK, s'il y en a. ⚠️ EN DERNIER, et c'est délibéré : c'est la seule écriture qui touche
  // de la marchandise. Échouée après les trois autres, elle laisse une gamme complète sans stock —
  // rattrapable d'un geste. Faite en premier, elle aurait fait entrer des pièces sur des articles qui
  // n'existent pas encore.
  const entrees = retenues
    .map((x) => ({ gencode: d.codes[x.k]!.trim(), qty: Number(d.qte[x.k] ?? '') || 0 }))
    .filter((x) => x.qty > 0);
  if (entrees.length > 0 && d.dest === 'stock') {
    await api.createStockEntries(entrees, `${lot}:stock`);
  }
}


/** Les trois destinations du relevé, et ce que chacune suppose côté données. */
const DESTINATIONS = [
  {
    k: 'stock' as const,
    t: key('Entrée directe en stock'),
    d: key('Disponible à la vente immédiatement.'),
    trace: 'M4 8h16v11H4zM2 4h20v4H2zM10 12h4',
    /**
     * ⚠️ SEULE DESTINATION CONSTRUITE, et ce n'est pas un arbitrage de confort : `stock_movement`
     * existe et le serveur sait déjà en émettre (il le fait pour les sorties de vente). Les deux
     * autres n'ont AUCUNE table — ni commande, ni bon de livraison — donc rien à écrire.
     */
    ouverte: true,
  },
  {
    k: 'cmd' as const,
    t: key('Commande fournisseur'),
    d: key('Nouvelle commande, ou une commande en cours.'),
    trace: 'M6 2h9l5 5v15H6zM14 2v6h6M9 13h6M9 17h6',
    ouverte: false,
  },
  {
    k: 'bl' as const,
    t: key('Bon de livraison'),
    d: key('Nouveau BL, ou un BL non encore entré en stock.'),
    trace: 'M2 7h12v8H2zM14 10h4l3 3v2h-7zM6.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3M17.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3',
    ouverte: false,
  },
];

/**
 * ÉTAPE 5 — COMBIEN, ET POUR ALLER OÙ.
 *
 * ⚠️ LES QUANTITÉS SONT FACULTATIVES, et l'écran le dit en tête : on crée très souvent le référentiel
 * d'une gamme avant d'en recevoir la moindre pièce. Les exiger obligerait à saisir des zéros, ou pire,
 * à inventer un stock pour passer l'étape.
 *
 * ⚠️ DEUX DESTINATIONS SUR TROIS SONT ANNONCÉES MAIS FERMÉES. Commande fournisseur et bon de livraison
 * n'ont AUCUNE table dans le schéma : les rendre choisissables ferait saisir des quantités qui
 * n'iraient nulle part. Elles restent visibles — le relevé les prévoit, et les masquer laisserait
 * croire que l'entrée directe est la seule façon de recevoir — mais elles DISENT qu'elles ne sont pas
 * construites, comme les portes du menu de création.
 */
function QuantitesGamme({ d, set, couleurs, axes }: {
  d: BrouillonGamme;
  set: <K extends keyof BrouillonGamme>(k: K, v: BrouillonGamme[K]) => void;
  couleurs: readonly string[];
  axes: readonly SizeAxis[];
}) {
  /** Ce que la grille de quantités AFFICHE en colonne : le libellé de chaque axe. */
  const sizes = useMemo(() => axes.map((a) => a.label), [axes]);
  /** Les cases RETENUES : une case sans code-barres ne sera pas créée, lui donner une quantité n'a pas de sens. */
  const retenues = useMemo(
    () => casesDeLaMatrice(couleurs, axes).filter((x) => (d.codes[x.k] ?? '') !== ''),
    [couleurs, axes, d.codes],
  );

  const total = useMemo(
    () => retenues.reduce((n, x) => n + (Number(d.qte[x.k] ?? '') || 0), 0),
    [retenues, d.qte],
  );

  const pa = parseMinor(d.pa, { scale: 4 });
  /** ⚠️ Le corner n'a pas de prix d'achat saisi : sa clause en tient lieu, et la note ne s'applique pas. */
  const sansPrixAchat = !d.corner && pa.state !== 'ok';

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap', marginBottom: 14,
      }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>
            
            {t('Combien, et pour aller où ?')}
          </div>
          <p style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
            
            {t('Laissez vide pour créer le référentiel sans mouvement de stock.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => set('qte', Object.fromEntries(retenues.map((x) => [x.k, '2'])))}
          style={{
            height: 32, padding: '0 12px', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px',
            fontWeight: 600, whiteSpace: 'nowrap', border: '1px solid var(--ink-200)',
            background: 'var(--paper)', color: 'var(--ink-700)', cursor: 'pointer',
          }}
        >{t('Mettre 2 partout')}</button>
      </div>

      <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <div style={{ display: 'inline-block', minWidth: '100%' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <div style={{ width: 128, flexShrink: 0 }} />
            {sizes.map((s) => (
              <div key={s} style={{
                width: 70, flexShrink: 0, textAlign: 'center', fontFamily: 'var(--font-mono)',
                fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-500)',
              }}>{s}</div>
            ))}
          </div>

          {couleurs.map((c) => (
            <div key={c} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <div style={{
                width: 128, flexShrink: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }} title={c}>{c}</div>
              {sizes.map((s) => {
                const k = cleCase(c, s);
                const creee = (d.codes[k] ?? '') !== '';
                return (
                  <div key={k} style={{ width: 70, flexShrink: 0 }}>
                    {creee ? (
                      <input
                        value={d.qte[k] ?? ''}
                        // ⚠️ CHIFFRES SEULEMENT, filtrés à la frappe : une quantité est un compte de
                        // pièces. « 2,5 » n'a pas de sens ici, et le laisser entrer ferait échouer
                        // l'écriture six écrans plus loin.
                        onChange={(e) => set('qte', { ...d.qte, [k]: e.target.value.replace(/[^0-9]/g, '') })}
                        inputMode="numeric"
                        placeholder="0"
                        aria-label={t('Quantité {c} {s}', { c, s })}
                        style={{
                          width: '100%', height: 42, boxSizing: 'border-box',
                          border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                          background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-control)',
                          fontWeight: 600, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                          outline: 'none',
                        }}
                      />
                    ) : (
                      // ⚠️ HACHURÉE, pas absente : la case existe dans la matrice mais n'a pas de code,
                      // donc elle ne sera pas créée. Un simple vide se lirait « quantité zéro ».
                      <div
                        title={t('{c} {s} — non créée, cette case n’a pas de code-barres', { c, s })}
                        style={{
                          width: '100%', height: 42, borderRadius: 'var(--r-ctrl)',
                          background: 'repeating-linear-gradient(45deg, var(--ink-50), var(--ink-50) 4px, var(--ink-100) 4px, var(--ink-100) 8px)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <span style={{
          display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5,
        }}>{t('Destination de ces quantités')}</span>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(194px, 1fr))', gap: 11,
          alignItems: 'stretch',
        }}>
          {DESTINATIONS.map((dest) => (
            <CarteDestination
              key={dest.k} dest={dest} choisie={d.dest === dest.k}
              onClick={() => set('dest', dest.k)}
              // ⚠️ ON DIT POURQUOI, ET CE N'EST PAS UN « bientôt » vague : il n'y a pas de table.
              onRefused={() => showToast(
                t('« {destination} » n’est pas encore construit : le schéma ne porte ni commande ni bon de livraison. Les quantités saisies n’iraient nulle part.', { destination: t(dest.t) }),
                'warn',
              )}
            />
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: '12.5px', color: 'var(--ink-600)' }}>
          
          <Trans
            k="Choisi : <1>{destination}</1>"
            vars={{ destination: (() => { const c = DESTINATIONS.find((x) => x.k === d.dest); return c ? t(c.t) : '—'; })() }}
            parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]}
          />
          {total > 0 && (
            <> <Trans k='· <1>{n}</1> pièce' vars={{ n: total }} parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} /></>
          )}
        </div>
      </div>

      {/* ⚠️ RELEVÉ MOT POUR MOT, et ce n'est pas un avertissement d'erreur : c'est autorisé, c'est
          signalé. Un PMP construit sans prix d'achat reste juste sur les quantités et faux sur la
          valeur — le dire ici évite de le découvrir dans un état de stock valorisé. */}
      {d.dest === 'stock' && total > 0 && sansPrixAchat && (
        <div style={{
          marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)',
          border: '1px solid var(--amber)', fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.55,
        }}>
          <Trans k='Entrée en stock sans prix d’achat : le prix moyen pondéré de ces articles sera marqué <1>partiellement estimé</1> jusqu’à la première réception valorisée. C’est autorisé, c’est simplement signalé.' parts={[(x) => <strong>{x}</strong>]} /></div>
      )}
    </div>
  );
}

/**
 * Une destination — carte à icône, titre, explication, et pastille de choix.
 * ⚠️ UNE DESTINATION FERMÉE S'ANNONCE INACTIVE ET SON CLIC EXPLIQUE, il ne choisit pas : la carte décide elle-même lequel
 *    des deux gestes part, puisque c'est elle qui annonce.
 */
function CarteDestination({ dest, choisie, onClick, onRefused }: {
  dest: (typeof DESTINATIONS)[number];
  choisie: boolean;
  onClick: () => void;
  onRefused: () => void;
}) {
  const ferme = !dest.ouverte;
  return (
    <button
      type="button" onClick={ferme ? onRefused : onClick} aria-disabled={ferme || undefined} aria-pressed={choisie}
      className={OZ_INTERACTIVE_SURFACE}
      style={{
        display: 'flex', flexDirection: 'column', gap: 10, padding: 16, textAlign: 'left',
        border: `1.5px solid ${choisie ? 'var(--teal-500)' : 'var(--ink-200)'}`,
        borderRadius: 'var(--r-sm)', background: 'var(--paper)',
        cursor: ferme ? 'not-allowed' : 'pointer', opacity: ferme ? 0.62 : 1,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%' }}>
        <span style={{
          width: 36, height: 36, borderRadius: 'var(--r-ctrl)', flexShrink: 0,
          background: choisie ? 'var(--teal-500)' : 'var(--ink-100)',
          color: choisie ? '#fff' : 'var(--ink-600)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d={dest.trace} />
          </svg>
        </span>
        <span style={{ flex: 1 }} />
        {choisie && (
          <span style={{
            width: 20, height: 20, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 12l5 5L20 6" />
            </svg>
          </span>
        )}
        {ferme && (
          <span style={{
            height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)',
            color: 'var(--ink-500)', fontSize: 'var(--t-sub)', fontWeight: 700, display: 'inline-flex',
            alignItems: 'center', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '.04em',
          }}>{t('bientôt')}</span>
        )}
      </span>
      <span style={{
        display: 'block', fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)', lineHeight: 1.3,
      }}>{t(dest.t)}</span>
      <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
        {t(dest.d)}
      </span>
    </button>
  );
}


/**
 * ÉTAPE 6 — CE QU'ON S'APPRÊTE À CRÉER, avant de le créer.
 *
 * ⚠️ ELLE NE RÉSUME PAS, ELLE VÉRIFIE. Cinq écrans plus tôt, l'opérateur a choisi une famille et un
 * fournisseur qu'il ne voit plus ; les trois chiffres du haut sont la dernière occasion de dire
 * « ce n'est pas ce que je voulais » avant que ça descende aux caisses.
 */
function RecapGamme({ d, cases, codePropose }: {
  d: BrouillonGamme;
  cases: readonly CaseMatrice[];
  codePropose: string;
}) {
  const retenues = cases.filter((x) => (d.codes[x.k] ?? '') !== '');
  const pieces = retenues.reduce((n, x) => n + (Number(d.qte[x.k] ?? '') || 0), 0);

  /**
   * LA VALEUR AU PRIX DE VENTE — sur le prix RÉEL de chaque case, jamais sur le prix commun seul.
   * ⚠️ Une gamme à prix variable (lingerie, grandes tailles) vaudrait sinon ce que vaut sa taille la
   * moins chère multipliée par tout le lot.
   */
  const valeur = useMemo(() => retenues.reduce((sum, x) => {
    const propre = parseMinor(d.pvParCase[x.k] ?? '', { scale: 2 });
    const commun = parseMinor(d.pvBase, { scale: 2 });
    const prix = propre.state === 'ok' ? propre.minor : (commun.state === 'ok' ? commun.minor : 0);
    return sum + prix * (Number(d.qte[x.k] ?? '') || 0);
  }, 0), [retenues, d.pvParCase, d.pvBase, d.qte]);

  const pv = parseMinor(d.pvBase, { scale: 2 });

  return (
    <div>
      <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>
        
        {t('Tout est prêt')}
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
        
        {t('Vérifiez, puis créez la gamme.')}
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12,
        marginBottom: 16,
      }}>
        <RecapTile heading={t('Déclinaisons')} valeur={String(retenues.length)} />
        <RecapTile heading={t('Pièces')} valeur={String(pieces)} />
        <RecapTile
          heading={t('Valeur au prix de vente')}
          valeur={valeur > 0 ? formatMoney(valeur, { thousandsSep: ' ' }) : '—'}
        />
      </div>

      <div style={{
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', flexShrink: 0,
      }}>
        <LigneRecap intitule={fieldLabel('name')} fort>{d.nom.trim() || codePropose}</LigneRecap>
        <LigneRecap intitule={fieldLabel('family')}>{d.famille || '—'}</LigneRecap>
        <LigneRecap intitule={fieldLabel('brand')}>{d.brand || '—'}</LigneRecap>
        <LigneRecap intitule={fieldLabel('supplier')}>{d.supplier || '—'}</LigneRecap>
        <LigneRecap intitule={fieldLabel('salePriceInclTax')} tabulaire>
          {pv.state === 'ok' ? formatMoney(pv.minor) : '—'}
        </LigneRecap>
        <LigneRecap intitule={t('Destination')} dernier>
          {pieces > 0 ? t('Entrée directe en stock') : t('Aucun mouvement de stock')}
        </LigneRecap>
      </div>

      {/* ⚠️ « AU PROCHAIN CONTACT », et pas « immédiatement » : les caisses tirent, le serveur ne
          pousse pas. Promettre l'instantané ferait chercher un défaut là où il n'y a qu'un délai. */}
      <div style={{
        marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
        border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55,
      }}>
        
        {t('La gamme descendra aux caisses au prochain contact.')}
      </div>
    </div>
  );
}

/** Un des trois chiffres du récapitulatif — gros, tabulaire, sans décor. */
function RecapTile({ heading, valeur }: { heading: string; valeur: string }) {
  return (
    <div style={{ padding: 16, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
        color: 'var(--ink-500)',
      }}>{heading}</div>
      <div style={{
        fontSize: 'var(--t-h1)', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--ink-900)',
        fontVariantNumeric: 'tabular-nums', marginTop: 5, whiteSpace: 'nowrap',
      }}>{valeur}</div>
    </div>
  );
}

/** Une ligne du tableau de vérification — intitulé à gauche, valeur à droite. */
function LigneRecap({ intitule, children, fort, tabulaire, dernier }: {
  intitule: string;
  children: React.ReactNode;
  fort?: boolean;
  tabulaire?: boolean;
  dernier?: boolean;
}) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '150px 1fr', gap: 14, padding: '12px 16px',
      fontSize: '13.5px',
      borderBottom: dernier ? undefined : '1px solid var(--ink-100)',
    }}>
      <div style={{ color: 'var(--ink-500)' }}>{intitule}</div>
      <div style={{
        minWidth: 0, color: fort ? 'var(--ink-900)' : 'var(--ink-800)',
        fontWeight: fort ? 600 : undefined,
        fontVariantNumeric: tabulaire ? 'tabular-nums' : undefined,
      }}>{children}</div>
    </div>
  );
}


/** Un intertitre de section — mono, capitales espacées : il sépare sans peser (relevé). */
function InterHeading({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
      color: 'var(--ink-500)', marginBottom: 7,
    }}>{children}</div>
  );
}

/**
 * ÉTAPE 4 — LE PRIX, COMMUN PAR DÉFAUT.
 *
 * ⚠️ LE COEFFICIENT N'EST PAS UNE DONNÉE, C'EST UN OUTIL DE SAISIE — même décision qu'à la fiche : on
 * tape un coefficient, il en déduit un prix, et c'est LE PRIX qui part. Le stocker créerait une
 * seconde vérité à côté du couple (prix d'achat, prix de vente), qui se contrediraient au premier
 * changement de tarif.
 *
 * ⚠️ TOUTE L'ARITHMÉTIQUE VIENT DES CŒURS, par `prix-derive` : la fiche article montre les mêmes
 * quatre indicateurs, et deux écrans qui les recalculeraient chacun de leur côté finiraient par
 * afficher deux coefficients pour un même article.
 *
 * ⚠️ EN CORNER, LE PRIX D'ACHAT EST UNE PROJECTION, et l'écran le nomme « théorique ». Le prix réel se
 * fige à chaque vente depuis le prix encaissé — c'est tout l'objet du régime, et la note le dit avec
 * un exemple chiffré sur la gamme en cours.
 */
function PrixGamme({ d, set, cases }: {
  d: BrouillonGamme;
  set: <K extends keyof BrouillonGamme>(k: K, v: BrouillonGamme[K]) => void;
  cases: readonly CaseMatrice[];
}) {
  const tvaBp = Number(d.tva) || 0;
  const pv = parseMinor(d.pvBase, { scale: 2 });
  const pa = parseMinor(d.pa, { scale: 4 });
  const ttc = pv.state === 'ok' ? pv.minor : null;
  const paSaisi = pa.state === 'ok' ? pa.minor : null;

  /**
   * LA CLAUSE DU FOURNISSEUR CHOISI À L’ÉTAPE 1 — lue, jamais saisie.
   * ⚠️ `undefined` = pas encore lue, `null` = lue et ABSENTE. Les confondre ferait annoncer
   * « aucun contrat » pendant la lecture, donc inviter à saisir ce qui allait arriver.
   * ⚠️ Best-effort ASSUMÉ : un échec de lecture laisse « N/C », ce qui est la vérité — on ne
   * fabrique pas un coefficient pour débloquer l’écran, puisque c’est lui qui fixerait le prix
   * d’achat de chaque vente.
   */
  const [contractCoeffBp, setContractCoeffBp] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    const nom = d.supplier.trim();
    if (!d.corner || nom === '') { setContractCoeffBp(null); return; }
    let vivant = true;
    setContractCoeffBp(undefined);
    api.supplierCoefficient(nom)
      .then((r) => { if (vivant) setContractCoeffBp(r.coefficientBp); })
      .catch(() => { if (vivant) setContractCoeffBp(null); });
    return () => { vivant = false; };
  }, [d.corner, d.supplier]);

  /** La clause EFFECTIVE : tant qu’elle se lit, il n’y en a pas encore — et rien ne se calcule. */
  const effectiveCoeffBp = contractCoeffBp ?? null;

  // ⚠️ EN CORNER, LE COÛT VIENT DU CONTRAT et de nulle part ailleurs : reprendre le coefficient tapé
  // afficherait une projection que la caisse ne suivra pas, puisqu’elle divisera par la clause réelle.
  const purchaseFine = d.corner
    ? achatTheoriqueCorner(ttc, effectiveCoeffBp)
    : paSaisi;
  const ind = indicateursPrix(ttc, purchaseFine, tvaBp, d.corner ? effectiveCoeffBp : null);

  return (
    <div>
      <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 3 }}>
        
        {t('À quel prix ?')}
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
        
        {t('Un prix commun suffit dans la plupart des cas.')}
      </p>

      <div style={{ maxWidth: 760 }}>
        <InterHeading>{fieldLabel('purchaseRegime')}</InterHeading>
        <div role="radiogroup" aria-label={fieldLabel('purchaseRegime')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <CarteRegime
            index={0} pick={(k) => set('corner', k === 1)}
            choisie={!d.corner} onClick={() => set('corner', false)}
            heading={t('Achat ferme')}
            aide={t('Le prix d’achat est fixé à l’entrée en stock — la remise en caisse ronge votre marge.')}
          />
          <CarteRegime
            index={1} pick={(k) => set('corner', k === 1)}
            choisie={d.corner} onClick={() => set('corner', true)}
            heading={t('Corner — PA calculé à la vente')}
            aide={t('Le coefficient est contractuel : chaque vente calcule son prix d’achat depuis le prix réellement encaissé — le fournisseur partage la remise.')}
          />
        </div>

        <InterHeading>{t('Tarif commun')}</InterHeading>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 13,
        }}>
          <ChampPrix
            label={d.corner ? t('Prix d’achat théorique') : fieldLabel('purchasePriceExclTax')}
            aide={d.corner
              ? t('Théorique — PV catalogue ÷ coef. Le PA réel se fige à chaque vente.')
              : t('Prix d’achat de la gamme — sert de base au coefficient.')}
          >
            {d.corner
              ? <PaveVerrouille valeur={purchaseFine === null ? '—' : formatMoney(rescale(purchaseFine, 4, 2), { thousandsSep: ' ' })} />
              : <SaisiePrix value={d.pa} onChange={(v) => set('pa', v)} placeholder="N/C" />}
          </ChampPrix>

          <ChampPrix
            label={t('Coefficient')}
            aide={!d.corner
              ? t('Saisir un coef recalcule le PV depuis le PA ; saisir un PV recalcule le coef.')
              : contractCoeffBp === undefined
                ? t('Lecture du contrat de ce fournisseur…')
                : contractCoeffBp !== null
                // ⚠️ « VERROUILLÉ » N'EST PLUS UN MOT : le coefficient vient VRAIMENT du contrat, et le
                // champ ne se saisit plus. Tant que rien n'écrivait `supplier.coefficient_bp`, afficher
                // un verrou aurait nommé un mécanisme inexistant — c'est désormais l'inverse qui serait
                // faux : laisser saisir ici produirait une clause de gamme que rien ne conserve.
                ? t('Coef verrouillé — contrat de {supplier}, posé aux Référentiels.', { supplier: d.supplier })
                : t('Aucun coef au contrat de {supplier} — à poser dans Référentiels › Fournisseurs & marques.', { supplier: d.supplier || t('ce fournisseur') })}
          >
            {d.corner
              ? <PaveVerrouille valeur={effectiveCoeffBp === null ? 'N/C' : `× ${(effectiveCoeffBp / 10_000).toFixed(2).replace('.', ',')}`} />
              : (
                <SaisiePrix
                  value={d.coeff}
                  onChange={(v) => {
                    set('coeff', v);
                    // ⚠️ EN FERME, le coefficient est un raccourci de saisie : il pose un prix. Le champ
                    // n'existe pas en corner, où c'est le contrat qui commande.
                    // Le coefficient en centièmes, par la porte unique : une décimale de trop se refuse, elle ne s'arrondit pas.
                    const lu = parseMinor(v, { scale: 2 });
                    const propose = ttcFromCoeff(paSaisi, lu.state === 'ok' && lu.minor > 0 ? lu.minor : null);
                    if (propose !== null) set('pvBase', toInputValue(propose, 2));
                  }}
                  placeholder="—"
                />
              )}
          </ChampPrix>

          <ChampPrix
            label={fieldLabel('salePriceInclTax')}
            aide={t('Commun à la gamme — TVA {v} % choisie à l’étape 1.', { v: (tvaBp / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) })}
          >
            <SaisiePrix value={d.pvBase} onChange={(v) => set('pvBase', v)} placeholder="69,00" />
          </ChampPrix>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: 11,
        marginTop: 18,
      }}>
        <Kpi heading={t('Prix HT')} valeur={ind.htCents === null ? '—' : formatMoney(ind.htCents, { thousandsSep: ' ' })} />
        <Kpi heading={t('Dont TVA')} valeur={ind.tvaCents === null ? '—' : formatMoney(ind.tvaCents, { thousandsSep: ' ' })} />
        <Kpi
          heading={t('Marge')} accent
          valeur={ind.margeBp === null ? '—' : `${(ind.margeBp / 100).toFixed(0)} %`}
          sous={ind.margeCents === null
            ? t('PA non renseigné')
            : t('{v} HT par pièce', { v: formatMoney(ind.margeCents, { thousandsSep: ' ' }) })}
        />
        <Kpi heading={t('Coefficient')} valeur={ind.coeffCents === null ? '—' : `× ${(ind.coeffCents / 100).toFixed(2).replace('.', ',')}`} />
      </div>

      {d.corner && (
        <div style={{
          marginTop: 14, padding: '13px 15px', borderRadius: 'var(--r-sm)',
          background: 'var(--violet-bg)', border: '1px solid var(--violet-fg)',
          fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55,
        }}>
          <Trans k='<1>Exemple sur cette gamme :</1> {v} La marge affichée en statistiques utilise le PA réel de chaque vente — jamais le théorique.' vars={{ v: exempleCorner(ttc, effectiveCoeffBp) }} parts={[(x) => <strong style={{ color: 'var(--violet-fg)' }}>{x}</strong>]} /></div>
      )}

      {/* Même rangée bordée à interrupteur que le drapeau web — 560 px au relevé. */}
      <Switch
        layout="card" style={{ marginTop: 16, maxWidth: 560 }}
        checked={d.prixVariable} onChange={(v) => set('prixVariable', v)}
        label={t('Le prix varie selon la déclinaison')}
        hint={t('Cas lingerie, grandes tailles, matières différentes.')}
      />

      {d.prixVariable && (
        <div style={{
          marginTop: 16, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 60px 132px', gap: 10, padding: '10px 14px',
            background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
          }}>
            {/* ⚠️ CLÉ PAR POSITION : deux champs surchargés vers le même mot donneraient deux clés égales. */}
            {[fieldLabel('color'), fieldLabel('size'), fieldLabel('salePriceInclTax')].map((mot, i) => (
              <div key={i} style={{
                fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
                textTransform: 'uppercase', color: 'var(--ink-500)',
              }}>{mot}</div>
            ))}
          </div>

          {/* ⚠️ SEULES LES CASES RETENUES : une case sans code-barres ne sera pas créée, lui demander
              un prix ferait remplir un tableau dont une partie ne produit rien. */}
          {cases.filter((x) => (d.codes[x.k] ?? '') !== '').map((x) => (
            <div key={x.k} style={{
              display: 'grid', gridTemplateColumns: '1fr 60px 132px', gap: 10, padding: '7px 14px',
              borderBottom: '1px solid var(--ink-100)', alignItems: 'center',
            }}>
              <div style={{
                fontSize: 'var(--t-lead)', color: 'var(--ink-800)', minWidth: 0, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }} title={x.c}>{x.c}</div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)',
              }}>{x.s}</div>
              <input
                value={d.pvParCase[x.k] ?? ''}
                onChange={(e) => set('pvParCase', { ...d.pvParCase, [x.k]: e.target.value })}
                inputMode="decimal"
                // Le prix commun sert de repère : une case laissée vide le reprend, et l'écran le montre
                // plutôt que de l'annoncer en note.
                placeholder={d.pvBase || '0,00'}
                aria-label={`${fieldLabel('salePriceInclTax')} ${x.c} ${x.s}`}
                style={{
                  height: 32, padding: '0 10px', boxSizing: 'border-box', width: '100%',
                  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
                  background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)',
                  textAlign: 'right', outline: 'none',
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{
        marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
        border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55,
      }}>
        <Trans k='Sans prix d’achat, marge et coefficient resteront <1>N/C</1> et le prix moyen pondéré sera signalé comme partiellement estimé.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/**
 * L'EXEMPLE CHIFFRÉ DU CORNER — ce que la remise fait au prix d'achat.
 * ⚠️ IL EST LE CŒUR DE L'ARGUMENT : en corner, remiser ne ronge pas la marge de la même façon, parce
 * que le prix d'achat baisse avec le prix de vente. Une phrase abstraite ne le fait pas comprendre ;
 * deux montants, si.
 * ⚠️ LE COEFFICIENT PASSE EN POINTS DE BASE, tel que le contrat le porte : l'arrondir aux centièmes ferait
 *    calculer l'exemple sur un autre coefficient que celui de la clause (1,3585 → 1,36).
 */
function exempleCorner(ttcCents: number | null, coeffBp: number | null): string {
  const pa = achatTheoriqueCorner(ttcCents, coeffBp);
  if (ttcCents === null || pa === null) return t('Renseignez le PV et le coef pour voir le calcul.');
  // 14 % de remise : le cas courant des soldes, assez marqué pour que l'écart se voie.
  const discount = mulDivRound(ttcCents, 86, 100);
  const discountedPurchasePrice = achatTheoriqueCorner(discount, coeffBp)!;
  const e = (fine: number) => formatMoney(rescale(fine, 4, 2), { thousandsSep: ' ' });
  return t('vendu {pv} le PA est {pa} ; remisé à {pvRemise} il tombe à {paRemise}.', {
    pv: formatMoney(ttcCents, { thousandsSep: ' ' }), pa: e(pa),
    pvRemise: formatMoney(discount, { thousandsSep: ' ' }), paRemise: e(discountedPurchasePrice),
  });
}

/** Un des deux régimes d'achat — carte à pastille, 230 px minimum au relevé. */
function CarteRegime({ index, pick, choisie, heading, aide, onClick }: {
  /** Son rang dans le groupe, et le choix par rang — les flèches du groupe passent par lui. */
  index: number; pick: (index: number) => void;
  choisie: boolean; heading: string; aide: string; onClick: () => void;
}) {
  return (
    <button
      type="button" role="radio" aria-checked={choisie} onClick={onClick}
      {...rovingRadio({ index, count: 2, checked: choisie, anyChecked: true, onPick: pick })}
      className={OZ_INTERACTIVE_SURFACE}
      style={{
        flex: 1, minWidth: 230, display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '11px 13px', textAlign: 'left', borderRadius: 'var(--r-sm)',
        border: `1px solid ${choisie ? 'var(--teal-500)' : 'var(--ink-200)'}`,
        background: 'var(--paper)', cursor: 'pointer',
      }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: 'var(--r-pill)', flexShrink: 0, marginTop: 1,
        border: `2px solid ${choisie ? 'var(--teal-500)' : 'var(--ink-300)'}`,
        background: choisie ? 'var(--teal-500)' : 'var(--paper)',
        boxShadow: choisie ? 'inset 0 0 0 2px var(--paper)' : 'none',
      }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</span>
        <span style={{
          display: 'block', fontSize: '11.5px', color: 'var(--ink-600)', lineHeight: 1.45, marginTop: 2,
        }}>{aide}</span>
      </span>
    </button>
  );
}

/** Un champ de la ligne tarifaire : intitulé, contrôle, puis son explication. */
function ChampPrix({ label, aide, children }: {
  label: string; aide: string; children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span style={{
        display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5,
      }}>{label}</span>
      {children}
      <span style={{
        display: 'block', fontSize: '11.5px', color: 'var(--ink-400)', marginTop: 5, lineHeight: 1.4,
      }}>{aide}</span>
    </label>
  );
}

/** Une saisie de montant — alignée à droite, comme tout ce qui se compare en colonne. */
function SaisiePrix({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <input
      value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal"
      placeholder={placeholder}
      style={{
        width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
        color: 'var(--ink-900)', fontSize: '13.5px', textAlign: 'right', outline: 'none',
      }}
    />
  );
}

/**
 * UNE VALEUR QUI NE SE SAISIT PAS — pointillé violet, la convention de l'écran Prix pour ce qui est
 * calculé ou contractuel. Un champ d'apparence normale inviterait à le corriger, en vain.
 */
function PaveVerrouille({ valeur }: { valeur: string }) {
  return (
    <span style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 40, padding: '0 12px',
      border: '1px dashed var(--violet-fg)', borderRadius: 'var(--r-ctrl)', background: 'var(--violet-bg)',
      color: 'var(--violet-fg)', fontSize: '13.5px', fontWeight: 700, boxSizing: 'border-box',
    }}>{valeur}</span>
  );
}

/** Un indicateur de la rangée — le chiffre d'abord, le nom au-dessus en petit. */
function Kpi({ heading, valeur, sous, accent }: {
  heading: string; valeur: string; sous?: string; accent?: boolean;
}) {
  return (
    <div style={{
      padding: 15, borderRadius: 'var(--r-sm)',
      border: `1px solid ${accent ? 'var(--teal-300)' : 'var(--ink-200)'}`,
      background: accent ? 'var(--teal-100)' : 'transparent',
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
        color: accent ? 'var(--teal-800)' : 'var(--ink-500)', marginBottom: 6,
      }}>{heading}</div>
      <div style={{
        fontSize: 'var(--t-h2)', fontWeight: 800, letterSpacing: '-0.025em',
        color: accent ? 'var(--teal-800)' : 'var(--ink-900)', fontVariantNumeric: 'tabular-nums',
      }}>{valeur}</div>
      {sous !== undefined && (
        <div style={{
          fontSize: '11.5px', color: 'var(--teal-800)', opacity: 0.8, marginTop: 2,
          fontVariantNumeric: 'tabular-nums',
        }}>{sous}</div>
      )}
    </div>
  );
}

/** Les deux raccourcis « Toutes » / « Aucune » — même gabarit, relevé à 30 px de haut. */
function BoutonMini({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        height: 30, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
        background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600,
        whiteSpace: 'nowrap', cursor: 'pointer',
      }}
    >{children}</button>
  );
}

/**
 * ÉTAPE 1 — l'identité, commune à toutes les déclinaisons.
 * ⚠️ MÊME GRILLE QUE LA MAQUETTE (`auto-fit minmax(230px,1fr)`), et 230 et non 220 : cet assistant est
 * plus large que la modale simple, ses champs le sont aussi.
 */
function Identite({ d, set, codePropose, offres, onCreateValue }: {
  d: BrouillonGamme;
  set: <K extends keyof BrouillonGamme>(k: K, v: BrouillonGamme[K]) => void;
  codePropose: string;
  offres: ArticleFacets | null;
  onCreateValue: (facet: ArticleFacet, value: string) => Promise<string>;
}) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
        <label style={{ display: 'block', gridColumn: '1/-1', minWidth: 0 }}>
          <Intitule>{fieldLabel('name')}</Intitule>
          <Saisie value={d.nom} onChange={(v) => set('nom', v)} placeholder={t('Chemise oxford')} />
        </label>

        <label style={{ display: 'block', minWidth: 0 }}>
          <Intitule>{fieldLabel('articleCode')}</Intitule>
          <Saisie value={d.code} onChange={(v) => set('code', v)} placeholder={codePropose} />
          {/* ⚠️ ÉNONCÉ, PAS DÉTECTÉ : `code` n'est pas une facette, le navigateur n'a pas la liste. La
              phrase est vraie dans tous les cas, et elle dit le comportement — qui n'est PAS une erreur. */}
          <Aide>{t('Porte toutes les tailles et couleurs. Proposé, modifiable — si le code existe déjà, la gamme sera rattachée à l’article existant.')}</Aide>
        </label>

        <Selecteur id="sect" label={fieldLabel('sector')} facette="sect"
          valeur={d.secteur} onChange={(v) => set('secteur', v)} offres={offres} onCreateValue={onCreateValue} />
        <Selecteur id="fam" label={fieldLabel('family')} facette="fam"
          valeur={d.famille} onChange={(v) => set('famille', v)} offres={offres} onCreateValue={onCreateValue} />
        <Selecteur id="marq" label={fieldLabel('brand')} facette="marq"
          valeur={d.brand} onChange={(v) => set('brand', v)} offres={offres} onCreateValue={onCreateValue} />
        <Selecteur id="sup" label={fieldLabel('supplier')} facette="sup"
          valeur={d.supplier} onChange={(v) => set('supplier', v)} offres={offres} onCreateValue={onCreateValue} />
        <Selecteur id="sais" label={fieldLabel('season')} facette="sais"
          valeur={d.saison} onChange={(v) => set('saison', v)} offres={offres} onCreateValue={onCreateValue} />

        <div style={{ minWidth: 0 }}>
          <Picker id="bo-wz-tva" variant="field" label={fieldLabel('vatRate')} options={TVA}
            value={d.tva} onChange={(v) => set('tva', v ?? '')} clearLabel="" allLabel="" />
        </div>

        {/* Rangée bordée pleine largeur, comme dans la modale simple : même drapeau, même forme. */}
        <Switch
          layout="card" style={{ gridColumn: '1/-1' }}
          checked={d.web} onChange={(v) => set('web', v)}
          label={t('Diffusé sur le site internet')}
          hint={t('Drapeau de l’article, comme « remises interdites ».')}
        />
      </div>

      {/* Relevé l.1520 — il dit ce que l'étape ne demande PAS, et pourquoi ce n'est pas un oubli. */}
      <div style={{
        marginTop: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)',
        background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55,
      }}>
        <Trans k='La marque reprend le fournisseur. Poids, origine et douane resteront <1>N/C</1> — complétables depuis la fiche.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </>
  );
}

function Intitule({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5,
    }}>{children}</span>
  );
}

function Saisie({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
        background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none',
      }}
    />
  );
}

/**
 * Un sélecteur de référentiel, avec création à la volée.
 * ⚠️ JAMAIS DANS UN `<label>` : `variant="field"` rend déjà son intitulé, et un label ré-active son
 * contrôle à chaque clic — le panneau devient impossible à fermer (constaté le 2026-08-20).
 */
function Selecteur({ id, label, facette, valeur, onChange, offres, onCreateValue }: {
  id: string; label: string; facette: ArticleFacet;
  valeur: string; onChange: (v: string) => void; offres: ArticleFacets | null;
  /** Crée la valeur ET l'offre à la liste (`useOfferedFacets`) — rend ce que le serveur a retenu. */
  onCreateValue: (facet: ArticleFacet, value: string) => Promise<string>;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <Picker
        id={`bo-wz-${id}`}
        variant="field"
        label={label}
        clearLabel={t('À choisir')}
        allLabel={t('À choisir')}
        options={(offres?.values[facette] ?? []).map((v) => ({ value: v.value, label: v.value }))}
        onSearch={(t) => api.articleFacetSearch(facette, t).then((r) => r.map((o) => ({ value: o.value, label: o.value })))}
        onCreate={async (v) => {
          // ⚠️ LA CIBLE VIENT DE LA FACETTE, JAMAIS D'UNE PROP — voir `referentiel-facette`.
          return onCreateValue(facette, v);
        }}
        unavailable={!offres}
        value={valeur}
        onChange={(nv) => onChange(nv ?? '')}
      />
    </div>
  );
}

function Aide({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '8px 0 0', fontSize: '11.5px', color: 'var(--ink-400)', lineHeight: 1.5 }}>{children}</p>;
}
